import { useLayoutEffect, useState } from 'react';

import { CHALLENGER_BREAKPOINTS } from '../media';

export type MobileKeyboardInset = {
  /** Layout-viewport pixels covered by the software keyboard. */
  inset: number;
  /** `visualViewport.offsetTop` — iOS often scrolls the visual viewport. */
  offsetTop: number;
  offsetLeft: number;
  height: number;
  width: number;
};

const EMPTY: MobileKeyboardInset = {
  inset: 0,
  offsetTop: 0,
  offsetLeft: 0,
  height: 0,
  width: 0,
};

const readMetrics = (): MobileKeyboardInset => {
  if (
    typeof window === 'undefined' ||
    window.innerWidth >= CHALLENGER_BREAKPOINTS.tabletMinPx
  ) {
    return EMPTY;
  }

  const viewport = window.visualViewport;
  if (!viewport) {
    return {
      inset: 0,
      offsetTop: 0,
      offsetLeft: 0,
      height: window.innerHeight,
      width: window.innerWidth,
    };
  }

  return {
    inset: Math.max(
      0,
      Math.round(window.innerHeight - viewport.height - viewport.offsetTop),
    ),
    offsetTop: Math.round(viewport.offsetTop),
    offsetLeft: Math.round(viewport.offsetLeft),
    height: Math.round(viewport.height),
    width: Math.round(viewport.width),
  };
};

/**
 * Tracks the mobile visual viewport so overlays can sit above the software
 * keyboard. iOS Safari often does not shrink `100dvh` when the keyboard opens
 * and instead shifts `visualViewport.offsetTop`.
 */
export const useMobileKeyboardInset = (
  enabled: boolean,
): MobileKeyboardInset => {
  const [metrics, setMetrics] = useState<MobileKeyboardInset>(EMPTY);

  useLayoutEffect(() => {
    if (!enabled) {
      setMetrics(EMPTY);
      return;
    }

    const update = () => setMetrics(readMetrics());
    update();

    const viewport = window.visualViewport;
    viewport?.addEventListener('resize', update);
    viewport?.addEventListener('scroll', update);
    window.addEventListener('resize', update);

    return () => {
      viewport?.removeEventListener('resize', update);
      viewport?.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, [enabled]);

  return metrics;
};
